import { useState, useRef } from 'react';
import useApi from '../useApi';
import ReactAudioPlayer from 'react-audio-player';
import { useChatContext } from '../../context/ChatContextProvider';

interface Text2AudioRequest {
  text: string;
  voiceId?: string;
  speed?: number;
  initAudio?: string | null;
}

interface AudioPlayerState {
  isPlaying: boolean;
  error: string | null;
  audioUrl: string | null;
}

export const VOICE_OPTIONS = [
  {
    id: 'bella',
    name: 'Bella',
  },
  {
    id: 'nicole',
    name: 'Nicole',
  },
  {
    id: 'sierra',
    name: 'Sierra',
  },
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RANDOM_FEMALE = 'ummahtoonsfemale67a4055fa';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MEAN_GIRL_VOICE_ID = 'meangirl';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FEMALE_1_VOICE_ID = 'female1';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FEMALE_2_VOICE_ID = 'female1';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SEXY_VOICE = 'sexy';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const HOT_VOICE_ID = 'meangirl';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BELLA = 'bella';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const NICOLE = 'nicole';
const DEFAULT_VOICE_ID = NICOLE;
const DEFAULT_SPEED = 1.0;
const IS_USING_MODEL_SLAB = true;
const DEFAULT_SPEED_MULTIPLIER = IS_USING_MODEL_SLAB ? 1.13 : 1;
const MAX_RETRY_ATTEMPTS = 15;
const RETRY_DELAY_MS = 3000;

const usePlayTextToAudio = () => {
  const { apiCall } = useApi('text2Audio', 'POST');
  //const { selectedVoice } = useChatContext();
  const [state, setState] = useState<AudioPlayerState>({
    isPlaying: false,
    error: null,
    audioUrl: null,
  });
  const { selectedVoice } = useChatContext();
  const audioPlayerRef = useRef<ReactAudioPlayer>(null);

  const playTextToAudio = async (
    text: string,
    voiceId: string = selectedVoice || DEFAULT_VOICE_ID,
    speed: number = DEFAULT_SPEED,
    speedUpMultiplier: number = DEFAULT_SPEED_MULTIPLIER,
  ): Promise<void> => {
    if (!text) return;

    setState({ isPlaying: true, error: null, audioUrl: null });

    try {
      const audioUrl = await fetchAudioUrl({
        text,
        voiceId,
        speed,
      });
      await playAudioWithRetry(audioUrl, speedUpMultiplier);
    } catch (err) {
      setState({
        isPlaying: false,
        error: err instanceof Error ? err.message : 'Unknown error occurred',
        audioUrl: null,
      });
    }
  };

  const fetchAudioUrl = async (
    requestBody: Text2AudioRequest,
  ): Promise<string> => {
    const response = await apiCall(requestBody);

    if (response?.data?.error) {
      throw new Error(response.data.error);
    }

    const audioUrl = response?.data?.audioUrl;
    if (!audioUrl) {
      throw new Error('No audio URL returned from the server');
    }

    return audioUrl;
  };

  const playAudioWithRetry = async (
    audioUrl: string,
    speedMultiplier: number,
    retryAttempt = 0,
  ): Promise<void> => {
    try {
      // Check if the audio file exists
      const response = await fetch(audioUrl, { method: 'HEAD' });

      if (!response.ok) {
        if (response.status === 404 && retryAttempt < MAX_RETRY_ATTEMPTS) {
          console.log(
            `Audio file not ready yet, retry ${
              retryAttempt + 1
            }/${MAX_RETRY_ATTEMPTS} in ${RETRY_DELAY_MS / 1000} seconds...`,
          );
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
          return playAudioWithRetry(
            audioUrl,
            speedMultiplier,
            retryAttempt + 1,
          );
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Set the audio URL and update state before playing
      setState({ isPlaying: true, error: null, audioUrl });

      // Use a small timeout to ensure the component has updated with the new URL
      setTimeout(() => {
        if (audioPlayerRef.current?.audioEl.current) {
          const audioElement = audioPlayerRef.current.audioEl.current;
          audioElement.playbackRate = speedMultiplier;
          audioElement.onended = () => {
            setState((prev) => ({ ...prev, isPlaying: false }));
          };

          // Use the play promise to catch any errors
          const playPromise = audioElement.play();

          if (playPromise !== undefined) {
            playPromise.catch((error) => {
              console.error('Audio playback failed:', error);
              setState({
                isPlaying: false,
                error: 'Failed to play audio',
                audioUrl,
              });
            });
          }
        }
      }, 100);
    } catch (err) {
      setState({
        isPlaying: false,
        error: err instanceof Error ? err.message : 'Unknown error occurred',
        audioUrl: null,
      });
    }
  };

  const stopAudio = () => {
    if (audioPlayerRef.current?.audioEl.current) {
      audioPlayerRef.current.audioEl.current.pause();
      audioPlayerRef.current.audioEl.current.currentTime = 0;
      setState((prev) => ({ ...prev, isPlaying: false }));
    }
  };

  return {
    playTextToAudio,
    stopAudio,
    isPlaying: state.isPlaying,
    error: state.error,
    audioUrl: state.audioUrl,
    audioPlayerRef,
  };
};

export default usePlayTextToAudio;
